import { useMemo } from 'react';
import './EQMap.css';

interface FrequencyData {
  frequency: number;
  amplitude: number;
}

interface EQMapProps {
  frequencies?: FrequencyData[];
  mixing?: {
    frequencySpectrum?: Record<string, number>;
  };
  instruments?: Array<{ type: string; frequencyRange?: { min: number; max: number } }>;
}

/**
 * 6-band frequency spectrum visualizer.
 * Bands are colored from red (low) → amber (mid) → cyan (high) — matching
 * the new spectral waveform palette so the two reads consistently.
 */

interface Band {
  id: string;
  label: string;
  range: string;
  min: number; // Hz
  max: number; // Hz
  rgb: [number, number, number];
}

const BANDS: Band[] = [
  { id: 'sub',     label: 'Sub',      range: '20–60 Hz',     min: 20,    max: 60,    rgb: [255, 60, 26] },
  { id: 'bass',    label: 'Bass',     range: '60–250 Hz',    min: 60,    max: 250,   rgb: [255, 110, 50] },
  { id: 'lowmid',  label: 'Low Mid',  range: '250–500 Hz',   min: 250,   max: 500,   rgb: [255, 170, 40] },
  { id: 'mid',     label: 'Mid',      range: '500 Hz – 2 kHz', min: 500, max: 2000,  rgb: [255, 215, 60] },
  { id: 'highmid', label: 'High Mid', range: '2 – 5 kHz',    min: 2000,  max: 5000,  rgb: [120, 220, 200] },
  { id: 'high',    label: 'High',     range: '5 – 20 kHz',   min: 5000,  max: 20000, rgb: [0,   217, 255] },
];

function bandEnergy(b: Band, freqData: FrequencyData[]): number {
  if (!freqData.length) return 0;
  let energy = 0;
  let n = 0;
  for (const f of freqData) {
    if (f.frequency >= b.min && f.frequency <= b.max) {
      energy += f.amplitude;
      n++;
    }
  }
  return n > 0 ? energy / n : 0;
}

function pillForInstrument(name: string): { tone: string; tint: string } {
  const lower = name.toLowerCase();
  if (lower.includes('kick') || lower.includes('drum') || lower.includes('bass')) {
    return { tone: 'rgba(255, 80, 40, 0.9)', tint: 'rgba(255, 80, 40, 0.14)' };
  }
  if (lower.includes('vocal') || lower.includes('lead') || lower.includes('synth')) {
    return { tone: 'rgba(255, 200, 50, 0.9)', tint: 'rgba(255, 200, 50, 0.14)' };
  }
  if (lower.includes('hi-hat') || lower.includes('cymbal') || lower.includes('hihat')) {
    return { tone: 'rgba(0, 217, 255, 0.9)', tint: 'rgba(0, 217, 255, 0.14)' };
  }
  if (lower.includes('pad')) {
    return { tone: 'rgba(157, 0, 255, 0.9)', tint: 'rgba(157, 0, 255, 0.14)' };
  }
  return { tone: 'rgba(255, 255, 255, 0.7)', tint: 'rgba(255, 255, 255, 0.08)' };
}

export function EQMap({ frequencies, mixing, instruments }: EQMapProps) {
  const freqData: FrequencyData[] = useMemo(() => {
    if (frequencies && frequencies.length) return frequencies;
    if (mixing?.frequencySpectrum) {
      return Object.entries(mixing.frequencySpectrum)
        .map(([f, a]) => ({
          frequency: parseFloat(f),
          amplitude: typeof a === 'number' ? a : 0,
        }))
        .sort((a, b) => a.frequency - b.frequency);
    }
    return [];
  }, [frequencies, mixing]);

  const bandValues = useMemo(() => {
    return BANDS.map((b) => ({
      ...b,
      value: bandEnergy(b, freqData),
    }));
  }, [freqData]);

  // Normalize so the loudest band paints to ~95% — keeps the chart readable
  // even when raw amplitude data is small.
  const max = Math.max(0.0001, ...bandValues.map((b) => b.value));
  const normalized = bandValues.map((b) => ({
    ...b,
    height: Math.max(8, (b.value / max) * 95),
  }));

  return (
    <div className="eq-map">
      <div className="eq-meta">
        <span className="eq-meta-label">Frequency Spectrum</span>
        <span className="eq-meta-value">EQ Map</span>
      </div>

      <div className="eq-stage">
        {/* dB grid lines */}
        <div className="eq-grid" aria-hidden="true">
          <span className="eq-grid-line" style={{ bottom: '0%' }} />
          <span className="eq-grid-line" style={{ bottom: '25%' }} />
          <span className="eq-grid-line" style={{ bottom: '50%' }} />
          <span className="eq-grid-line" style={{ bottom: '75%' }} />
        </div>

        {/* 6 band columns */}
        <div className="eq-columns">
          {normalized.map((b) => {
            const [r, g, blue] = b.rgb;
            const top = `rgba(${r}, ${g}, ${blue}, 0.95)`;
            const bottom = `rgba(${r}, ${g}, ${blue}, 0.20)`;
            return (
              <div key={b.id} className="eq-column">
                <div className="eq-column-track">
                  <div
                    className="eq-column-fill"
                    style={{
                      height: `${b.height}%`,
                      background: `linear-gradient(180deg, ${top} 0%, ${bottom} 100%)`,
                      boxShadow: `0 0 18px rgba(${r}, ${g}, ${blue}, 0.30)`,
                    }}
                  />
                </div>
                <div className="eq-column-meta">
                  <span className="eq-column-label">{b.label}</span>
                  <span className="eq-column-range">{b.range}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Instrument frequency-range pills */}
      {instruments && instruments.length > 0 && (
        <div className="eq-instruments">
          {instruments.map((inst, idx) => {
            if (!inst.frequencyRange) return null;
            const { tone, tint } = pillForInstrument(inst.type);
            return (
              <span
                key={`${inst.type}-${idx}`}
                className="eq-instrument-pill"
                style={{ borderColor: tone, background: tint, color: tone }}
                title={`${inst.frequencyRange.min} – ${inst.frequencyRange.max} Hz`}
              >
                <span className="eq-instrument-name">{inst.type}</span>
                <span className="eq-instrument-range">
                  {inst.frequencyRange.min < 1000
                    ? `${inst.frequencyRange.min}`
                    : `${(inst.frequencyRange.min / 1000).toFixed(1)}k`}
                  –
                  {inst.frequencyRange.max < 1000
                    ? `${inst.frequencyRange.max} Hz`
                    : `${(inst.frequencyRange.max / 1000).toFixed(1)}k`}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
